import type { ReactNode } from 'react';

import { Fragment } from 'react';

import { Box, Typography, CircularProgress } from '@mui/material';

import { DashboardCard } from '../../components/dashboard-card/dashboard-card';

type Props<T extends { id: string | number }> = {
  testId: string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  onClick?: () => void;
  badge?: string;
  comingSoon?: boolean;
  items: T[];
  loading?: boolean;
  emptyIcon: ReactNode;
  emptyLabel: string;
  columns: number;
  gap: number;
  renderItem: (item: T) => ReactNode;
};

/** A dashboard card with the latest files in a fixed grid. */
export function RecentFilesCard<T extends { id: string | number }>({
  testId,
  title,
  subtitle,
  icon,
  onClick,
  badge,
  comingSoon = false,
  items,
  loading = false,
  emptyIcon,
  emptyLabel,
  columns,
  gap,
  renderItem,
}: Props<T>) {
  return (
    <DashboardCard
      data-testid={testId}
      title={title}
      subtitle={subtitle}
      icon={icon}
      onClick={onClick}
      badge={badge}
      comingSoon={comingSoon}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 1,
            color: 'text.secondary',
          }}
        >
          {emptyIcon}
          <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.4 }}>
            {emptyLabel}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap,
          }}
        >
          {items.map((item) => (
            <Fragment key={item.id}>{renderItem(item)}</Fragment>
          ))}
        </Box>
      )}
    </DashboardCard>
  );
}
